import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface BaseDetailSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  maxWidthClass?: string;
  zIndex?: number;
}

const BaseDetailSheet: React.FC<BaseDetailSheetProps> = ({
  isOpen,
  onClose,
  children,
  maxWidthClass = 'max-w-md md:max-w-[42rem]',
  zIndex = 9999,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (isOpen) {
      setShouldRender(true);

      animationFrameRef.current = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });

      return () => {
        if (animationFrameRef.current !== null) {
          window.cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      };
    }

    setIsVisible(false);
    closeTimerRef.current = setTimeout(() => {
      setShouldRender(false);
      closeTimerRef.current = null;
    }, 320);

    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [isOpen]);

  const handleSheetTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || event.propertyName !== 'transform') {
      return;
    }

    if (!isOpen && !isVisible) {
      setShouldRender(false);
    }
  };

  if (!shouldRender) {
    return null;
  }

  return createPortal(
    <div
      className={`fixed inset-0 transition-opacity duration-300 ease-out pt-[env(safe-area-inset-top)] pb-[calc(var(--app-footer-h)+env(safe-area-inset-bottom))] md:pb-[env(safe-area-inset-bottom)] ${isVisible ? 'bg-black/60 opacity-100' : 'bg-black/0 opacity-0 pointer-events-none'}`}
      style={{ zIndex }}
      onClick={onClose}
    >
      <div
        className="h-full overflow-y-auto overscroll-none flex items-end md:items-center justify-center px-3 md:px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full flex justify-center">
          <div
            className={`w-full ${maxWidthClass} transform transition-transform duration-300 ease-out ${
              isVisible ? 'translate-y-0' : 'translate-y-full'
            } md:translate-y-0 bg-gray-900 border border-gray-700 rounded-t-2xl md:rounded-2xl p-5 space-y-4 max-h-[calc(100dvh-var(--app-footer-h)-env(safe-area-inset-top)-env(safe-area-inset-bottom)-12px)] md:max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-32px)] overflow-y-auto overflow-x-hidden overscroll-none scrollable-content will-change-transform`}
            onTransitionEnd={handleSheetTransitionEnd}
          >
            {children}
            <div style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)' }} />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BaseDetailSheet;
